import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, StatusBadge, GsdReadableBlock } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { EntityCrudPanel, demoFlag } from "@/components/admin/EntityCrudPanel.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useDataStore } from "@/components/admin/useDataStore.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { getDemoContextForAccount } from "@/data/dojoOsOrgModel.js";
import { findMembershipForUsername } from "@/membership/membershipStore.js";
import {
  updateProgression,
  updateProgressCriterion,
  setExamRecommendation,
} from "@/data/dataStore.js";
import { notifyDataUpdate } from "@/components/admin/useDataStore.js";
import {
  PROGRESS_STATUSES,
  CRITERION_STATUSES,
  EXAM_STATUSES,
  CRITERION_LABELS,
  BELT_STATUS_LABELS,
  progressLabel,
} from "@/utils/progressLabels.js";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { localizeDemoText } from "@/i18n/localizeDemoText.js";

const SENSEI_DOJO_ID = "demo-dojo-01";

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

export function useProgressScope() {
  const { username, role, activeRoleUi } = useAuth();
  const ctx = getDemoContextForAccount(username);
  const membershipFound = findMembershipForUsername(username);
  const effectiveRole = activeRoleUi || role;

  return useMemo(() => {
    if (role === "hanshi" || role === "admin" || effectiveRole === "hanshi") {
      return { mode: "admin" };
    }
    if (effectiveRole === "sensei" || role === "sensei") {
      return { mode: "sensei", dojoId: ctx?.dojoId || membershipFound.membership?.identity?.dojoId || SENSEI_DOJO_ID };
    }
    if (effectiveRole === "assistant" || role === "assistant") {
      return { mode: "sempai", dojoId: ctx?.dojoId || SENSEI_DOJO_ID, readOnly: true };
    }
    if (effectiveRole === "parent" || role === "parent") {
      return { mode: "parent", childIds: ctx?.childStudentIds || [] };
    }
    if (effectiveRole === "student" || role === "student") {
      return { mode: "student", studentId: ctx?.studentId || membershipFound.membership?.studentId };
    }
    return { mode: "guest" };
  }, [role, effectiveRole, ctx]);
}

function beltName(store, beltId, locale) {
  const b = store.ceintures.find((c) => c.id === beltId);
  return localizeDemoText(b?.nom ?? beltId, locale);
}

export function BeltsListPanel({ allowCrud = false, testId = "karate-belts-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const rows = [...store.ceintures].sort((a, b) => a.ordre - b.ordre);

  const columns = [
    {
      key: "nom",
      label: locale === "en" ? "Belt" : "Ceinture",
      render: (r) => (
        <>
          <span className={`belt-swatch belt-swatch--${r.couleur}`} aria-hidden="true" /> {r.nom}{" "}
          {demoFlag(r)}
        </>
      ),
    },
    { key: "ordre", label: locale === "en" ? "Order" : "Ordre" },
    { key: "niveau", label: locale === "en" ? "Grade" : "Grade" },
    { key: "style", label: "Style" },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => (
        <StatusBadge
          label={progressLabel(BELT_STATUS_LABELS, r.statut, locale)}
          tone={r.statut === "actif" ? "green" : r.statut === "futur" ? "neutral" : "yellow"}
          testId={`belt-status-${r.id}`}
        />
      ),
    },
    {
      key: "exigences",
      label: locale === "en" ? "Requirements (demo)" : "Exigences (démo)",
      render: (r) => <span className="muted">{r.exigences?.slice(0, 60)}…</span>,
    },
  ];

  if (allowCrud) {
    return (
      <div data-testid={testId}>
        <p className="muted" data-testid="belts-demo-notice">
          {locale === "en"
            ? "Demo belt requirements — official referential pending validation."
            : "Exigences démo — référentiel officiel à valider."}
        </p>
        <EntityCrudPanel
          collectionKey="ceintures"
          rows={store.ceintures}
          testId="admin-ceintures-crud"
          searchFields={["nom", "couleur", "niveau"]}
          columns={columns}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "nom", label: locale === "en" ? "Name" : "Nom", required: true },
            { name: "couleur", label: locale === "en" ? "Color" : "Couleur", required: true },
            { name: "ordre", label: locale === "en" ? "Order" : "Ordre", required: true },
            { name: "niveau", label: "Kyu/Dan", required: true },
            { name: "style", label: "Style", required: true },
            { name: "exigences", label: locale === "en" ? "Requirements" : "Exigences" },
            {
              name: "statut",
              label: locale === "en" ? "Status" : "Statut",
              options: Object.entries(BELT_STATUS_LABELS).map(([v, l]) => ({
                value: v,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            nom: form.nom.trim(),
            couleur: form.couleur.trim(),
            ordre: Number(form.ordre) || 0,
            niveau: form.niveau.trim(),
            style: form.style.trim(),
            exigences: form.exigences.trim(),
            statut: form.statut || "actif",
            demo: true,
          })}
        />
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <p className="muted">{locale === "en" ? "Demo belt ladder — not official." : "Échelle démo — non officielle."}</p>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["nom", "couleur", "niveau"]}
        emptyMessage={locale === "en" ? "No belts found." : "Aucune ceinture trouvée."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

export function ProgressOverviewPanel({ scope: scopeProp, testId = "karate-progress-overview" }) {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const store = useDataStore();
  const internalScope = useProgressScope();
  const scope = scopeProp ?? internalScope;

  const rows = useMemo(() => {
    return store.progressions
      .map((p) => {
        const student = store.etudiants.find((e) => e.id === p.student_id);
        if (!student) return null;
        if (scope.mode === "sensei" && student.dojo_id !== scope.dojoId) return null;
        if (scope.mode === "sempai" && student.dojo_id !== scope.dojoId) return null;
        if (scope.mode === "student" && p.student_id !== scope.studentId) return null;
        if (scope.mode === "parent" && !scope.childIds?.includes(p.student_id)) return null;
        return {
          ...p,
          studentName: `${student.prenom} ${student.nom}`,
          dojo_id: student.dojo_id,
        };
      })
      .filter(Boolean);
  }, [store.progressions, store.etudiants, scope]);

  const columns = [
    {
      key: "studentName",
      label: locale === "en" ? "Student" : "Étudiant",
      render: (r) => localizeDemo(r.studentName),
    },
    {
      key: "dojo_id",
      label: "Dojo",
      render: (r) => localizeDemo(store.dojos.find((d) => d.id === r.dojo_id)?.nom ?? r.dojo_id),
    },
    {
      key: "ceinture_actuelle_id",
      label: locale === "en" ? "Current belt" : "Ceinture actuelle",
      render: (r) => beltName(store, r.ceinture_actuelle_id, locale),
    },
    {
      key: "prochaine_ceinture_id",
      label: locale === "en" ? "Next belt" : "Prochaine ceinture",
      render: (r) => beltName(store, r.prochaine_ceinture_id, locale),
    },
    {
      key: "pourcentage_progression",
      label: "%",
      render: (r) => `${r.pourcentage_progression}%`,
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => progressLabel(PROGRESS_STATUSES, r.statut, locale),
    },
    {
      key: "recommandation_examen",
      label: locale === "en" ? "Exam" : "Examen",
      render: (r) =>
        r.recommandation_examen ? (
          <StatusBadge label={locale === "en" ? "Recommended" : "Recommandé"} tone="blue" testId={`rec-${r.student_id}`} />
        ) : (
          "—"
        ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      searchFields={["studentName", "statut"]}
      filters={[
        {
          field: "statut",
          label: locale === "en" ? "Status" : "Statut",
          options: Object.entries(PROGRESS_STATUSES).map(([v, l]) => ({
            value: v,
            label: locale === "en" ? l.en : l.fr,
          })),
        },
      ]}
      emptyMessage={locale === "en" ? "No progression records." : "Aucune progression trouvée."}
      testId={testId}
    />
  );
}

export function ProgressDetailPanel({
  studentId,
  canEditSensei = false,
  canEditSempaiNote = false,
  testId = "karate-progress-detail",
}) {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const store = useDataStore();
  const prog = store.progressions.find((p) => p.student_id === studentId);
  const student = store.etudiants.find((e) => e.id === studentId);
  const [sempaiNote, setSempaiNote] = useState(prog?.notes_sempai || "");

  if (!prog || !student) {
    return (
      <p className="muted" data-testid={`${testId}-empty`}>
        {locale === "en" ? "No progression data." : "Aucune donnée de progression."}
      </p>
    );
  }

  const linkedExam = store.examens.find(
    (e) => e.etudiants_inscrits.includes(studentId) && e.statut !== "futur",
  );
  const linkedEvent = linkedExam?.event_id
    ? store.events.find((ev) => ev.id === linkedExam.event_id)
    : null;

  const handleCriterion = (key, statut) => {
    if (!canEditSensei) return;
    updateProgressCriterion(studentId, key, { statut });
    notifyDataUpdate();
  };

  const handleRecommend = () => {
    if (!canEditSensei) return;
    setExamRecommendation(studentId, !prog.recommandation_examen);
    notifyDataUpdate();
  };

  const handleSempaiNote = () => {
    if (!canEditSempaiNote) return;
    updateProgression(studentId, { notes_sempai: sempaiNote });
    notifyDataUpdate();
  };

  return (
    <GsdReadableBlock testId={testId}>
      <p className="muted" data-testid="progress-demo-notice">
        {locale === "en" ? "Demo progression — not official certification." : "Progression [DÉMO] — pas de certification officielle."}{" "}
        <DemoBadge />
      </p>
      <div className="progress-summary">
        <p>
          <strong>{localizeDemo(`${student.prenom} ${student.nom}`.trim())}</strong>
        </p>
        <p>
          {locale === "en" ? "Current belt" : "Ceinture actuelle"} :{" "}
          <strong>{beltName(store, prog.ceinture_actuelle_id, locale)}</strong>
        </p>
        <p>
          {locale === "en" ? "Next belt" : "Prochaine ceinture"} :{" "}
          <strong>{beltName(store, prog.prochaine_ceinture_id, locale)}</strong>
        </p>
        <p>
          {locale === "en" ? "Overall progress" : "Progression globale"} : <strong>{prog.pourcentage_progression}%</strong> —{" "}
          {progressLabel(PROGRESS_STATUSES, prog.statut, locale)}
        </p>
        {prog.notes_sensei && canEditSensei ? (
          <p data-testid="progress-sensei-notes">
            {locale === "en" ? "Sensei notes" : "Notes Sensei"} : {prog.notes_sensei}
          </p>
        ) : null}
      </div>

      <h3>{locale === "en" ? "Criteria" : "Critères"}</h3>
      <table className="gsd-admin-panel__table data-table" data-testid="progress-criteria-table">
        <thead>
          <tr>
            <th>{locale === "en" ? "Category" : "Catégorie"}</th>
            <th>{locale === "en" ? "Status" : "Statut"}</th>
            <th>{locale === "en" ? "Note" : "Note"}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(CRITERION_LABELS).map(([key, labels]) => {
            const c = prog.criteres[key] || { statut: "non_commence", note: "" };
            return (
              <tr key={key} data-testid={`criterion-row-${key}`}>
                <td>{locale === "en" ? labels.en : labels.fr}</td>
                <td>
                  {canEditSensei ? (
                    <select
                      value={c.statut}
                      onChange={(e) => handleCriterion(key, e.target.value)}
                      data-testid={`criterion-status-${key}`}
                    >
                      {Object.entries(CRITERION_STATUSES).map(([v, l]) => (
                        <option key={v} value={v}>
                          {locale === "en" ? l.en : l.fr}
                        </option>
                      ))}
                    </select>
                  ) : (
                    progressLabel(CRITERION_STATUSES, c.statut, locale)
                  )}
                </td>
                <td className="muted">{c.note || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {canEditSensei ? (
        <div className="btn-row" style={{ marginTop: "1rem" }}>
          <button type="button" className="btn-primary" onClick={handleRecommend} data-testid="recommend-exam-btn">
            {prog.recommandation_examen
              ? locale === "en"
                ? "Cancel exam recommendation"
                : "Retirer recommandation examen"
              : locale === "en"
                ? "Recommend for exam (demo)"
                : "Recommander pour examen (démo)"}
          </button>
        </div>
      ) : null}

      {canEditSempaiNote ? (
        <div className="sempai-note-form" data-testid="sempai-observation-form">
          <label>
            {locale === "en" ? "Sempai observation (no validation)" : "Observation Sempai (sans validation)"}
            <textarea
              value={sempaiNote}
              onChange={(e) => setSempaiNote(e.target.value)}
              rows={2}
              data-testid="sempai-observation-input"
            />
          </label>
          <button type="button" className="btn-secondary btn-sm" onClick={handleSempaiNote}>
            {locale === "en" ? "Save observation" : "Enregistrer observation"}
          </button>
        </div>
      ) : prog.notes_sempai ? (
        <p className="muted">{locale === "en" ? "Sempai" : "Sempai"} : {prog.notes_sempai}</p>
      ) : null}

      {linkedExam ? (
        <div className="progress-linked-exam" data-testid="progress-linked-exam">
          <h3>{locale === "en" ? "Linked exam" : "Examen lié"}</h3>
          <p>
            {linkedExam.titre} — {linkedExam.date}
          </p>
          {linkedEvent ? (
            <p>
              {locale === "en" ? "Event" : "Événement"} :{" "}
              <Link to="/admin/evenements">{linkedEvent.titre}</Link>
            </p>
          ) : null}
        </div>
      ) : null}
    </GsdReadableBlock>
  );
}

export function ExamsListPanel({ scope: scopeProp, allowCrud = false, testId = "karate-exams-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const internalScope = useProgressScope();
  const scope = scopeProp ?? internalScope;

  const rows = useMemo(() => {
    let list = store.examens;
    if (scope.mode === "sensei" || scope.mode === "sempai") {
      list = list.filter((e) => e.dojo_id === (scope.dojoId || SENSEI_DOJO_ID));
    }
    if (scope.mode === "student" && scope.studentId) {
      list = list.filter((e) => e.etudiants_inscrits.includes(scope.studentId));
    }
    if (scope.mode === "parent" && scope.childIds?.length) {
      list = list.filter((e) => e.etudiants_inscrits.some((id) => scope.childIds.includes(id)));
    }
    return list.map((e) => ({
      ...e,
      eventTitre: e.event_id ? store.events.find((ev) => ev.id === e.event_id)?.titre : "—",
      gradeLabel: beltName(store, e.grade_vise_id, locale),
      responsableNom: store.instructeurs.find((i) => i.id === e.responsable_id)?.nom ?? "—",
    }));
  }, [store.examens, store.events, store.instructeurs, store.ceintures, scope, locale]);

  const columns = [
    {
      key: "titre",
      label: locale === "en" ? "Title" : "Titre",
      render: (r) => (
        <>
          {r.titre} {demoFlag(r)}
        </>
      ),
    },
    { key: "date", label: locale === "en" ? "Date" : "Date" },
    { key: "gradeLabel", label: locale === "en" ? "Target grade" : "Grade visé" },
    {
      key: "etudiants_inscrits",
      label: locale === "en" ? "Registered" : "Inscrits",
      render: (r) => r.etudiants_inscrits.length,
    },
    { key: "responsableNom", label: locale === "en" ? "Lead" : "Responsable" },
    {
      key: "eventTitre",
      label: locale === "en" ? "Linked event" : "Événement lié",
      render: (r) => (
        <span data-testid={r.event_id ? `exam-event-link-${r.id}` : undefined}>{r.eventTitre}</span>
      ),
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => (
        <StatusBadge
          label={progressLabel(EXAM_STATUSES, r.statut, locale)}
          tone={r.statut === "futur" ? "neutral" : "blue"}
          testId={`exam-status-${r.id}`}
        />
      ),
    },
  ];

  const dojoOptions = store.dojos.map((d) => ({ value: d.id, label: d.nom }));
  const beltOptions = store.ceintures.map((b) => ({ value: b.id, label: b.nom }));
  const eventOptions = store.events
    .filter((e) => e.type === "examen")
    .map((e) => ({ value: e.id, label: e.titre }));

  if (allowCrud) {
    return (
      <div data-testid={testId}>
        <p className="muted" data-testid="exams-demo-notice">
          {locale === "en"
            ? "Light exams — no payment, no official certification."
            : "Examens légers — pas de paiement, pas de certification officielle."}
        </p>
        <EntityCrudPanel
          collectionKey="examens"
          rows={store.examens}
          testId="admin-examens-crud"
          searchFields={["titre", "id"]}
          columns={columns}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "titre", label: locale === "en" ? "Title" : "Titre", required: true },
            { name: "date", label: locale === "en" ? "Date" : "Date", type: "date", required: true },
            { name: "dojo_id", label: "Dojo", required: true, options: dojoOptions },
            { name: "grade_vise_id", label: locale === "en" ? "Target belt" : "Ceinture visée", options: beltOptions },
            { name: "event_id", label: locale === "en" ? "Linked event" : "Événement lié", options: [{ value: "", label: "—" }, ...eventOptions] },
            {
              name: "statut",
              label: locale === "en" ? "Status" : "Statut",
              options: Object.entries(EXAM_STATUSES).map(([v, l]) => ({
                value: v,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            { name: "notes", label: "Notes" },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            titre: form.titre.trim(),
            date: form.date,
            dojo_id: form.dojo_id,
            grade_vise_id: form.grade_vise_id,
            event_id: form.event_id || null,
            etudiants_inscrits: [],
            statut: form.statut || "planifie",
            responsable_id: "demo-sensei-placeholder",
            notes: form.notes.trim(),
            demo: true,
          })}
        />
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["titre", "statut"]}
        filters={[
          {
            field: "statut",
            label: locale === "en" ? "Status" : "Statut",
            options: Object.entries(EXAM_STATUSES).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No exams found." : "Aucun examen trouvé."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

export function StudentProgressView({ testId = "student-progress-view" }) {
  const scope = useProgressScope();
  if (!scope.studentId) return null;
  return <ProgressDetailPanel studentId={scope.studentId} testId={testId} />;
}

export function ParentProgressView({ testId = "parent-progress-view" }) {
  const locale = useLocale();
  const scope = useProgressScope();
  const store = useDataStore();
  const children = store.etudiants.filter((e) => scope.childIds?.includes(e.id));

  return (
    <GsdReadableBlock testId={testId}>
      <p className="muted" data-testid="parent-progress-no-payment">
        {locale === "en"
          ? "Summary only — exam payments not managed in Phase 1."
          : "Résumé uniquement — paiements d'examen non gérés en Phase 1."}
      </p>
      {children.map((child) => {
        const prog = store.progressions.find((p) => p.student_id === child.id);
        if (!prog) return null;
        return (
          <div key={child.id} data-testid={`parent-progress-child-${child.id}`}>
            <h3>
              {child.prenom} {child.nom} <DemoBadge />
            </h3>
            <p>
              {locale === "en" ? "Current" : "Actuelle"} : {beltName(store, prog.ceinture_actuelle_id, locale)} →{" "}
              {beltName(store, prog.prochaine_ceinture_id, locale)} ({prog.pourcentage_progression}%)
            </p>
            <p>{progressLabel(PROGRESS_STATUSES, prog.statut, locale)}</p>
            {prog.recommandation_examen ? (
              <StatusBadge
                label={locale === "en" ? "Exam recommended" : "Examen recommandé"}
                tone="blue"
                testId={`parent-exam-rec-${child.id}`}
              />
            ) : null}
          </div>
        );
      })}
    </GsdReadableBlock>
  );
}
